/**
* author: <iastley@squiz.net>
*
* enables the uploading of multiple files
*/

export default class MultiFileUpload {
    constructor (selector, maxsize) {
        this.selector = selector;
        this.inputs = [ ...document.querySelectorAll(selector) ];
        this.maxsize = maxsize ? maxsize : 10000;

        if (this.inputs.length) {
            // kickoff
            this._init();
        }
    }

    /////////////////////////////////
    // -- initiator

    /**
    * kicks off the class and provides
    * dynamic settings and context for other
    * methods
    */
    _init () {
        const forms = [];
        const groups = [];

        // first get the outermost forms
        this.inputs.forEach((node) => {
            if (node.dataset.multiUpload === "hidden") {
                node.classList.add("hidden");
            }

            const currentForm = this._parentForm(node);

            if (!forms.includes(currentForm)) {
                forms.push(currentForm);
            }
        });

        // get the forms and the upload inputs and generate data
        // and set some events
        forms.forEach((node) => {
            const inputs = [ ...node.querySelectorAll(this.selector) ];
            const formInfo = {
                form: node,
                inputs: [],
                sizes: [],
                size: 0,
                hiddenInputs: [],
                visibleInputs: [],
                valid: true
            };

            node.addEventListener("submit", this._onSubmit.bind(this, formInfo));

            inputs.forEach(input => { 
                const { multiUpload } = input.dataset;

                if (multiUpload === "hidden") {
                    //formInfo.hiddenInputs = formInfo.hiddenInputs + 1;
                    formInfo.hiddenInputs.push(input);
                } else {
                    formInfo.visibleInputs.push(input);
                }

                formInfo.inputs.push(input);
                input.addEventListener("change", this._onChange.bind(this, formInfo));
            });

            if (formInfo.hiddenInputs.length) {
                const addInputButton = document.createElement("button");

                formInfo.addButton = addInputButton;

                addInputButton.type = "button";
                addInputButton.textContent = "Add file +";
                addInputButton.setAttribute("aria-label", "add file");
                addInputButton.style.cssText = "display: block;";
                addInputButton.classList.add("_add-input-btn");

                addInputButton.addEventListener("click", this._addInput.bind(this, formInfo));

                formInfo.inputs.at(-1).after(addInputButton);
            }
        });
    }

    /////////////////////////////////
    // -- events

    /**
    * reveals a new hidden file upload field
    */
    _addInput = (data, e) => {
        this.current = data;

        if (!this.current.hiddenInputs.length) return;

        this.current.hiddenInputs[0].dataset.multiUpload = "visible";

        this._updateInputVisibility();
    }

    /**
    * when a form input changes set the current
    * form info and update state
    */
    _onChange = (data, e) => {
        const { target } = e;
        const files = target.files;

        this.current = data;

        this._update(files, target);
    }

    /**
    * submits the form if the file uploads haven't
    * exceeded the limit
    */
    _onSubmit = (formInfo, e) => {
        const { valid } = formInfo;

        if (!valid) e.preventDefault();
    }

    /////////////////////////////////
    // -- state management

    /**
    * this method updates the current form's info
    * it will check that file sizes are within the
    * set boundaries
    *
    * @param {object} files
    * the files that have been selected
    *
    * @param {HTMLElement} the current input 
    * file target
    *
    * @return {void}
    */
    _update (files, target) {
        const index = this.current.inputs.indexOf(target);

        if (!files.length) { 
            delete this.current.sizes[index];

            this.current.sizes = this.current.sizes.filter(Boolean);

            this._calculateSize();

            if (!this.current.valid) {
                target.classList.add("_error-text")
            } else {
                this.current.inputs.forEach(input => input.classList.remove("_error-text"))
            }

            return; 
        }

        const { size } = files[0];

        this.current.sizes[index] = size;

        this._calculateSize();

        if (!this.current.valid) {
            target.classList.add("_error-text")
        } else {
            this.current.inputs.forEach(input => input.classList.remove("_error-text"))
        }
    }

    /**
    * calculates the entire size of the current
    * form's upload fields and determines if the
    * form is valid for submission
    */
    _calculateSize () {
        this.current.size = this.current.sizes.length ? this.current.sizes.reduce((prev, current) => prev + current) : [];

        this.current.valid =  this.maxsize > this.current.size;
    }

    /**
    * updates the visibility of each file
    * upload input
    *
    * @return {void}
    */
    _updateInputVisibility () {
        // unshift formerly hidden fields into the visible ones
        this.current.hiddenInputs.forEach((input, i) => {
            const { multiUpload } = input.dataset;

            if (multiUpload === "visible") {
                input.classList.remove("hidden");
                this.current.visibleInputs.unshift(input);

                delete this.current.hiddenInputs[i];

                this.current.hiddenInputs = this.current.hiddenInputs.filter(Boolean);

                if (!this.current.hiddenInputs.length) {
                    this.current.addButton.classList.add("hidden");
                }
            }
        });

        // push formerly visibile fields into the hidden ones
        this.current.visibleInputs.forEach((input, i) => {
            const { multiUpload } = input.dataset;

            if (multiUpload === "hidden") {
                input.classList.add("hidden");
                this.current.hiddenInputs.push(input);

                delete this.current.visibleInputs[i];

                this.current.visibleInputs = this.current.visibleInputs.filter(Boolean);

                if (!this.current.visibleInputs.length) {
                    this.current.addButton.classList.remove("hidden");
                }
            }
        });
    }

    /////////////////////////////////
    // -- helpers

    /**
    * gets the input's current parent form
    *
    * @param {HTMLElement} child
    * the input to get the ancestor form from
    *
    * @return {HTMLElement|null}
    */
    _parentForm (child) {
        let parent = child;

        while (parent) {
            if (!parent.parentNode) {
                return null;
            }

            if (parent.nodeName.match(/form/i)) {
                return parent;
            }

            parent = parent.parentNode;
        }

        return null;
    }
}
